import React, { useState } from 'react';
import { X } from 'lucide-react';
import { AssetTypeRecord } from '../types';

interface Props {
  types: AssetTypeRecord[];
  onClose: () => void;
  onSubmit: (data: {
    asset_number: string;
    asset_type_id: string;
    asset_domain?: string;
    name: string;
    description?: string;
    manufacturer?: string;
    model?: string;
    serial_number?: string;
    criticality?: string;
    owner_user_name?: string;
    department?: string;
    current_location?: string;
    aircraft?: {
      registration_mark: string;
      msn: string;
      icao_type_code?: string;
      max_takeoff_weight?: number;
    };
  }) => Promise<void>;
}

export const AssetCreateModal: React.FC<Props> = ({ types, onClose, onSubmit }) => {
  const [assetNumber, setAssetNumber] = useState('');
  const [assetTypeId, setAssetTypeId] = useState(types[0]?.id || 'AT-AC-320');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [criticality, setCriticality] = useState('HIGH');
  const [currentLocation, setCurrentLocation] = useState('LTBA - Main Technical Maintenance Hangar');

  // Aircraft-specific
  const selectedType = types.find((t) => t.id === assetTypeId);
  const isAircraft = selectedType?.domain === 'AIRCRAFT';

  const [registrationMark, setRegistrationMark] = useState('');
  const [msn, setMsn] = useState('');
  const [icaoCode, setIcaoCode] = useState('A320');
  const [mtow, setMtow] = useState(77000);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await onSubmit({
        asset_number: assetNumber,
        asset_type_id: assetTypeId,
        asset_domain: selectedType?.domain,
        name,
        description: description || undefined,
        manufacturer: manufacturer || undefined,
        model: model || undefined,
        serial_number: serialNumber || undefined,
        criticality,
        current_location: currentLocation || undefined,
        aircraft: isAircraft
          ? {
              registration_mark: registrationMark,
              msn,
              icao_type_code: icaoCode,
              max_takeoff_weight: Number(mtow),
            }
          : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating asset record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Register Asset Inventory Item</h3>
            <p>Add Aircraft, Ground Support Equipment (GSE), Tooling, or Facilities</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-grid-2">
              <div className="form-field">
                <label>Asset Number / Tag *</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. AST-TC-XYZ / GSE-042"
                  value={assetNumber}
                  onChange={(e) => setAssetNumber(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Asset Type & Category *</label>
                <select
                  value={assetTypeId}
                  onChange={(e) => setAssetTypeId(e.target.value)}
                >
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      [{t.domain}] {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Asset Title & Name *</label>
              <input
                required
                type="text"
                placeholder="e.g. Airbus A320-200 or Calibrated Torque Wrench 1/2 in"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="form-grid-3">
              <div className="form-field">
                <label>Manufacturer</label>
                <input
                  type="text"
                  placeholder="e.g. Airbus / Snap-on"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Model / Variant</label>
                <input
                  type="text"
                  placeholder="e.g. A320-214 / TE-500"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Serial / Part Number</label>
                <input
                  type="text"
                  placeholder="e.g. SN-98124"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Operational Criticality</label>
                <select
                  value={criticality}
                  onChange={(e) => setCriticality(e.target.value)}
                >
                  <option value="CRITICAL">CRITICAL (Flight / Safety Critical)</option>
                  <option value="HIGH">HIGH (High Priority)</option>
                  <option value="MEDIUM">MEDIUM (Standard Operational)</option>
                  <option value="LOW">LOW (Low Impact)</option>
                </select>
              </div>
              <div className="form-field">
                <label>Current Station / Location</label>
                <input
                  type="text"
                  value={currentLocation}
                  onChange={(e) => setCurrentLocation(e.target.value)}
                />
              </div>
            </div>

            {/* Aircraft Parameters if Domain is AIRCRAFT */}
            {isAircraft && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                <div className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                  <span>✈️</span> Aircraft Specific Parameters (ICAO / EASA Regulatory Registry)
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Registration Mark (Tail) *</label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. TC-JHK"
                      value={registrationMark}
                      onChange={(e) => setRegistrationMark(e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Manufacturer Serial Number (MSN) *</label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. MSN-5491"
                      value={msn}
                      onChange={(e) => setMsn(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>ICAO Type Code</label>
                    <input
                      type="text"
                      value={icaoCode}
                      onChange={(e) => setIcaoCode(e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Max Takeoff Weight (MTOW kg)</label>
                    <input
                      type="number"
                      value={mtow}
                      onChange={(e) => setMtow(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Saving...' : 'Register Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
